import React from "react";
import { Link } from "react-router-dom";
import { SUB_PROJECTS, VENTURES, MEDIA_PROJECTS, PODCASTS, ASSETS } from "@/lib/brand";
import Seo from "@/components/Seo";

export default function Projects() {
  return (
    <div data-testid="projects-page" className="pt-32 pb-20">
      <Seo
        title="Projects · Companies, Shows & Sounds"
        description="The full slate from the Office of Miguel Santana — TruFlights, Fresh Start Credit, Skyline, Skyline TV, Standard Society, Athlete City, the TV/Book projects Same World and Built Different, and the podcasts Raise The Standard, Dating Rx, The Voice of Reason, and Miguel Talks Sports."
        path="/projects"
      />
      {/* Hero */}
      <section className="px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-6">— Projects</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[7.5rem] text-silver leading-[0.9] tracking-tighter max-w-6xl">
            The full slate.<br />
            <span className="italic font-light">Companies, shows & sounds.</span>
          </h1>
          <p className="mt-12 max-w-3xl font-body text-silver-muted text-lg leading-relaxed">
            Beyond MPR Ventures' five operating companies, the Office of Miguel Santana
            operates a deeper portfolio of consumer brands, technology products, books, series,
            and podcasts. Below is the working index.
          </p>
        </div>
      </section>

      {/* Operating brands under each venture */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="font-overline text-champagne mb-3">— Under the Conglomerate</p>
              <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
                Operating brands<br />
                <span className="italic font-light">within each holding.</span>
              </h2>
            </div>
            <Link to="/ventures" className="btn-editorial">View Parent Ventures →</Link>
          </div>

          <div className="space-y-20">
            {VENTURES.filter((v) => (SUB_PROJECTS[v.id] || []).length > 0).map((v) => (
              <div key={v.id} data-testid={`projects-group-${v.id}`} className="grid grid-cols-12 gap-6 md:gap-12">
                <div className="col-span-12 lg:col-span-4">
                  <p className="font-overline text-silver-muted mb-3">{v.city} · {v.region}</p>
                  <h3 className="font-display text-3xl md:text-4xl text-silver tracking-tight leading-[1.05]">{v.name}</h3>
                  <div className="mt-4 h-px w-16 bg-champagne"></div>
                  <p className="mt-6 font-body text-silver-muted text-sm leading-relaxed max-w-sm">{v.tagline}</p>
                  <Link to={`/ventures#${v.id}`} className="mt-6 inline-block font-overline text-silver hover:text-champagne transition-colors">
                    Open {v.name.split(" ")[0]} →
                  </Link>
                </div>
                <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border hairline">
                  {(SUB_PROJECTS[v.id] || []).map((p, i) => (
                    <div key={p.name} data-testid={`sub-project-${p.name.toLowerCase().replace(/\s+/g, '-')}`} className="bg-navy p-8 group hover:bg-navy-elevated transition-colors duration-300">
                      <div className="flex items-start justify-between">
                        <p className="font-overline text-champagne">{p.tagline}</p>
                        <span className="font-overline text-silver-muted">{String(i + 1).padStart(2, "0")}</span>
                      </div>
                      <h4 className="mt-4 font-display text-3xl md:text-4xl text-silver tracking-tight leading-[1.05]">{p.name}</h4>
                      <p className="mt-4 font-body text-silver-muted text-sm leading-relaxed">{p.description}</p>
                      {p.url && (
                        <a href={p.url} target="_blank" rel="noreferrer" className="mt-6 inline-flex font-overline text-silver group-hover:text-champagne transition-colors">
                          Visit →
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TV & Book Projects */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-overline text-champagne mb-3">— Television & Books</p>
          <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05] mb-12">
            Storytelling at scale.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border hairline">
            {MEDIA_PROJECTS.map((p, i) => (
              <article key={p.name} data-testid={`media-project-${p.name.toLowerCase().replace(/\s+/g, '-')}`} className="bg-navy p-10 md:p-12 relative overflow-hidden">
                <div className="absolute -top-4 -right-2 font-display text-[10rem] md:text-[14rem] text-silver/5 leading-none select-none" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="font-overline text-champagne">{p.type}</p>
                <h3 className="mt-4 font-display text-5xl md:text-6xl text-silver tracking-tighter leading-[1.02]">"{p.name}"</h3>
                <p className="mt-6 font-body text-silver-muted text-lg leading-relaxed max-w-md">{p.tagline}</p>
                <p className="mt-8 font-overline text-silver-muted">In Development</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Podcasts */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <p className="font-overline text-champagne mb-3">— On the Mics</p>
              <h2 className="font-display text-4xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
                The podcast slate.
              </h2>
            </div>
            <p className="font-overline text-silver-muted">Distributed on Spotify · Apple Podcasts</p>
          </div>

          <div className="border-y hairline">
            {PODCASTS.map((p, i) => (
              <div key={p.name} data-testid={`podcast-${p.name.toLowerCase().replace(/\s+/g, '-')}`} className="grid grid-cols-12 gap-6 items-center py-10 border-b hairline last:border-b-0 hover:bg-white/[0.025] transition-colors duration-300 px-2">
                <div className="col-span-12 md:col-span-1">
                  <p className="font-overline text-silver-muted">{String(i + 1).padStart(2, "0")}</p>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <h3 className="font-display text-2xl md:text-4xl text-silver leading-[1.05] tracking-tight">{p.name}</h3>
                  <p className="mt-2 font-body text-silver-muted max-w-md">{p.tagline}</p>
                </div>
                <div className="col-span-12 md:col-span-5 flex md:justify-end gap-4 flex-wrap">
                  <a href={p.spotify} target="_blank" rel="noreferrer" className="btn-editorial" data-testid={`podcast-${p.name.toLowerCase().replace(/\s+/g, '-')}-spotify`}>Spotify ↗</a>
                  <a href={p.apple} target="_blank" rel="noreferrer" className="btn-editorial" data-testid={`podcast-${p.name.toLowerCase().replace(/\s+/g, '-')}-apple`}>Apple Podcasts ↗</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="mt-24 md:mt-32 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto border hairline-strong p-10 md:p-16 bg-navy-surface grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-8">
            <p className="font-overline text-champagne mb-4">— Partnerships</p>
            <h2 className="font-display text-3xl md:text-5xl text-silver tracking-tighter leading-[1.05]">
              Want to invest, partner, or feature one of the projects?
            </h2>
            <p className="mt-6 font-body text-silver-muted max-w-2xl">
              The Office of Miguel Santana reviews collaboration opportunities for each
              brand individually. Press, investment, distribution, and booking inquiries are all
              welcome.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end">
            <Link to="/contact" data-testid="projects-cta" className="btn-editorial btn-editorial-filled">
              Book / Inquire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
